import { Component, OnChanges, SimpleChanges, Input, AfterViewInit } from '@angular/core';
import { MAPBOX_ACESS_TOKEN } from 'app/app.constants';
import * as mapboxgl from 'mapbox-gl';


@Component({
  selector: 'app-mini-mapa-geojson',
  standalone: true,
  templateUrl: './mini-mapa-geojson.component.html',
  styleUrls: ['./mini-mapa-geojson.component.css']
})
export class MiniMapaGeojsonComponent implements OnChanges, AfterViewInit {
  @Input() geojsonData?: any;

  public map!: mapboxgl.Map;
  public mapboxAccessToken: string = MAPBOX_ACESS_TOKEN
  public zoom: number = 12;
  public center: [number, number] = [-48.795, -22.6010];
  public mapStyle: string = 'mapbox://styles/mapbox/satellite-streets-v11?optimize=true';
  public geojsonSource: any;
  public maxBounds: any;

  ngAfterViewInit() {
    this.inicializarMapa();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['geojsonData'] && this.map) {
      this.carregarGeojsonNoMapa(this.geojsonData);
    }
  }

  public carregarGeojsonNoMapa(geojson: any): void {
    if (!this.map || !geojson) return;

    const sourceId = 'geojson-source';
    const fillLayerId = 'geojson-layer';
    const outlineLayerId = 'geojson-outline';

    this.removerCamadasExistentes([fillLayerId, outlineLayerId], sourceId);

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson,
    });

    if (geojson.features?.[0]?.geometry?.type !== 'LineString') {
      this.map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': '#24AB4A',
          'fill-opacity': 0.5,
          'fill-antialias': true,
        },
      });
    }

    this.map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#00F900',
        'line-width': 2,
      },
    });

    this.posicionarGeojsonNoMapa();
  }

  public posicionarGeojsonNoMapa(): void {
    if (!this.map || !this.geojsonData?.features?.length) return;

    const coordinates = this.extrairCoordenadas();
    if (coordinates.length === 0) return;

    const bounds = this.calcularBounds(coordinates);
    if (this.map.fitBounds) {
      this.map.fitBounds(bounds, { padding: 50 });
    }
  }

  private extrairCoordenadas(): number[][] {
    return this.geojsonData.features.flatMap((feature: any) => {
      const { geometry } = feature;
      if (!geometry) return [];

      switch (geometry.type) {
        case 'Polygon':
          return geometry.coordinates[0];
        case 'MultiPolygon':
          return geometry.coordinates.flatMap((polygon: any) => polygon[0]);
        case 'LineString':
          return geometry.coordinates;
        default:
          return [];
      }
    });
  }

  private calcularBounds(coordinates: number[][]): mapboxgl.LngLatBoundsLike {
    const lons = coordinates.map(coord => coord[0]);
    const lats = coordinates.map(coord => coord[1]);

    const minLon = lons.reduce((a, b) => Math.min(a, b), Infinity);
    const maxLon = lons.reduce((a, b) => Math.max(a, b), -Infinity);

    const minLat = lats.reduce((a, b) => Math.min(a, b), Infinity);
    const maxLat = lats.reduce((a, b) => Math.max(a, b), -Infinity);

    return [
      [minLon, minLat],
      [maxLon, maxLat]
    ];
  }

  private removerCamadasExistentes(layerIds: string[], sourceId: string): void {
    if (!this.map) return;

    for (const layerId of layerIds) {
      if (this.map.getLayer && this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    }

    if (this.map.getSource && this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId);
    }
  }

  private inicializarMapa(): void {
    this.map = new mapboxgl.Map({
      container: 'map',
      style: this.mapStyle,
      center: this.center,
      zoom: this.zoom,
      accessToken: this.mapboxAccessToken,
      attributionControl: false
    });
  }
}
